/**
 * Loyalty portal web component — mounts on /pages/loyalty.
 *
 * Renders:
 *  - Big balance number + lifetime earned/redeemed.
 *  - Active redemption tiers as click-to-redeem buttons.
 *  - Ledger history table (last 50 entries).
 *
 * On redeem: shows a modal with the generated code + copy-to-clipboard.
 */

import { LoyaltyClient, describeLoyaltyError } from './loyalty-client-v2.js';

const LEDGER_KIND_LABELS = {
  earn_purchase: 'Order',
  earn_review: 'Review',
  earn_welcome: 'Welcome bonus',
  earn_manual: 'Adjustment',
  reverse_purchase: 'Order cancelled',
  redeem: 'Redeemed',
  migration_import: 'Migration',
};

class LoyaltyPortal extends HTMLElement {
  constructor() {
    super();
    this.client = new LoyaltyClient();
  }

  async connectedCallback() {
    if (this.dataset.mounted === '1') return;
    this.dataset.mounted = '1';

    this.refs = {
      loading: this.querySelector('[data-loading]'),
      error: this.querySelector('[data-error]'),
      errorMessage: this.querySelector('[data-error-message]'),
      content: this.querySelector('[data-content]'),
      balance: this.querySelector('[data-balance]'),
      lifetimeEarned: this.querySelector('[data-lifetime-earned]'),
      lifetimeRedeemed: this.querySelector('[data-lifetime-redeemed]'),
      totals: this.querySelector('[data-totals]'),
      emptyCta: this.querySelector('[data-empty-cta]'),
      redeemSection: this.querySelector('[data-redeem]'),
      tiers: this.querySelector('[data-tiers]'),
      history: this.querySelector('[data-history]'),
      ledger: this.querySelector('[data-ledger]'),
      retry: this.querySelector('[data-retry]'),
      modal: this.querySelector('[data-redeem-modal]'),
      modalCode: this.querySelector('[data-modal-code]'),
      modalCodeFallback: this.querySelector('[data-modal-code-fallback]'),
      modalDiscount: this.querySelector('[data-modal-discount]'),
      modalExpires: this.querySelector('[data-modal-expires]'),
      modalCopy: this.querySelector('[data-modal-copy]'),
      modalCopyFallback: this.querySelector('[data-modal-copy-fallback]'),
      modalClose: this.querySelector('[data-modal-close]'),
    };

    this.refs.retry?.addEventListener('click', () => this.refresh());
    this.refs.modalCopy?.addEventListener('click', () => this.copyCode());
    this.refs.modalCopyFallback?.addEventListener('click', () => this.copyCode(this.refs.modalCopyFallback));
    this.refs.modalClose?.addEventListener('click', () => this.closeModal());
    this.refs.modal?.addEventListener('click', (e) => {
      if (e.target === this.refs.modal) this.closeModal();
    });

    await this.refresh();
  }

  showError(err) {
    if (this.refs.loading) this.refs.loading.hidden = true;
    if (this.refs.content) this.refs.content.hidden = true;
    if (this.refs.error) this.refs.error.hidden = false;
    if (this.refs.errorMessage) {
      this.refs.errorMessage.textContent = describeLoyaltyError(err);
    }
  }

  showContent() {
    if (this.refs.loading) this.refs.loading.hidden = true;
    if (this.refs.error) this.refs.error.hidden = true;
    if (this.refs.content) this.refs.content.hidden = false;
  }

  async refresh() {
    if (this.refs.loading) this.refs.loading.hidden = false;
    if (this.refs.content) this.refs.content.hidden = true;
    if (this.refs.error) this.refs.error.hidden = true;

    try {
      const [balance, tiers, ledger] = await Promise.all([
        this.client.getBalance(),
        this.client.getTiers(),
        this.client.getLedger({ limit: 50 }),
      ]);
      this.renderBalance(balance);
      this.renderTiers(tiers.tiers ?? [], balance.currentBalance ?? 0);
      this.renderLedger(ledger.entries ?? []);
      this.showContent();
    } catch (err) {
      // Graceful degradation: any failure (network, 5xx, deploy in progress)
      // falls through to the welcome state. Customer always sees the
      // educational How-it-works cards + the CTA. We don't surface a scary
      // "We couldn't load your points" panel — that one was reserved for
      // genuine signed-out / auth-rejected paths handled by Liquid.
      console.warn('[loyalty-portal] data load failed, showing welcome state', err);
      this.renderBalance({ currentBalance: 0, lifetimeEarned: 0, lifetimeRedeemed: 0 });
      this.renderTiers([], 0);
      this.renderLedger([]);
      this.showContent();
    }
  }

  renderBalance(balance) {
    const current = balance.currentBalance ?? 0;
    const earned = balance.lifetimeEarned ?? 0;
    const redeemed = balance.lifetimeRedeemed ?? 0;

    if (this.refs.balance) this.refs.balance.textContent = current.toLocaleString();
    if (this.refs.lifetimeEarned) this.refs.lifetimeEarned.textContent = earned.toLocaleString();
    if (this.refs.lifetimeRedeemed) this.refs.lifetimeRedeemed.textContent = redeemed.toLocaleString();

    // Welcome state: never earned or redeemed anything. Show CTA, hide
    // totals + the redeem-tier buttons (the "How it works" Redeem card
    // below already shows the tier ladder for educational purposes).
    const isFresh = earned === 0 && redeemed === 0;
    if (this.refs.emptyCta) this.refs.emptyCta.hidden = !isFresh;
    if (this.refs.totals) this.refs.totals.hidden = isFresh;
    if (this.refs.redeemSection) this.refs.redeemSection.hidden = isFresh;
  }

  renderTiers(tiers, currentBalance) {
    if (!this.refs.tiers) return;
    this.refs.tiers.innerHTML = '';
    for (const tier of tiers) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'loyalty-portal__tier';
      const affordable = currentBalance >= tier.points_cost;
      btn.disabled = !affordable;
      btn.dataset.points = String(tier.points_cost);
      btn.innerHTML = `
        <span class="loyalty-portal__tier-points">${tier.points_cost.toLocaleString()} pts</span>
        <span class="loyalty-portal__tier-arrow">&rarr;</span>
        <span class="loyalty-portal__tier-value">$${(tier.discount_value_cents / 100).toFixed(0)} off</span>
      `;
      btn.addEventListener('click', () => this.redeem(tier.points_cost));
      this.refs.tiers.appendChild(btn);
    }
  }

  renderLedger(entries) {
    if (!this.refs.ledger) return;
    // Hide the entire history section when there's nothing to show — the
    // "How it works" cards below explain everything anyway.
    if (entries.length === 0) {
      if (this.refs.history) this.refs.history.hidden = true;
      return;
    }
    if (this.refs.history) this.refs.history.hidden = false;

    const tbody = this.refs.ledger.querySelector('tbody') ?? this.refs.ledger;
    tbody.innerHTML = '';
    for (const e of entries) {
      const tr = document.createElement('tr');
      const date = new Date(e.created_at).toLocaleDateString();
      const sign = e.amount > 0 ? '+' : '';
      const amount = `${sign}${e.amount.toLocaleString()}`;
      const label = LEDGER_KIND_LABELS[e.kind] ?? e.kind;
      let detail = '';
      if (e.kind === 'redeem' && e.discount_code) {
        detail = `Code <code>${e.discount_code}</code>`;
      } else if (e.kind === 'earn_purchase' && e.order_id) {
        detail = `Order #${e.order_id}`;
      }
      tr.innerHTML = `
        <td>${date}</td>
        <td>${label}</td>
        <td>${detail}</td>
        <td class="loyalty-portal__amount loyalty-portal__amount--${e.amount >= 0 ? 'pos' : 'neg'}">${amount}</td>
      `;
      tbody.appendChild(tr);
    }
  }

  async redeem(points) {
    if (!confirm(`Redeem ${points} points?`)) return;
    try {
      const result = await this.client.redeem(points);
      // Try to auto-apply to cart so the customer doesn't have to copy/paste.
      // Shopify's Ajax /cart/update.js with {discount} sets the code on the
      // cart (and also works on empty carts — discount holds for first item).
      const applied = await applyDiscountToCart(result.code);
      this.openModal(result, applied);
      // Refresh in background to update balance + ledger
      void this.refresh();
    } catch (err) {
      alert(describeLoyaltyError(err));
    }
  }

  openModal(result, autoApplied) {
    if (!this.refs.modal) return;
    this.refs.modal.hidden = false;
    if (this.refs.modalCode) this.refs.modalCode.textContent = result.code;
    if (this.refs.modalCodeFallback) this.refs.modalCodeFallback.textContent = result.code;
    if (this.refs.modalDiscount)
      this.refs.modalDiscount.textContent = `$${(result.discount_value_cents / 100).toFixed(2)} off`;
    if (this.refs.modalExpires) {
      const exp = new Date(result.expires_at);
      this.refs.modalExpires.textContent = exp.toLocaleDateString();
    }
    // Toggle "applied to cart" view vs the copy-the-code fallback.
    this.refs.modal.dataset.autoApplied = autoApplied ? 'true' : 'false';
  }

  closeModal() {
    if (this.refs.modal) this.refs.modal.hidden = true;
  }

  async copyCode(buttonEl) {
    const code = this.refs.modalCode?.textContent ?? '';
    if (!code) return;
    const btn = buttonEl ?? this.refs.modalCopy;
    try {
      await navigator.clipboard.writeText(code);
      if (btn) {
        const original = btn.textContent;
        btn.textContent = 'Copied!';
        setTimeout(() => {
          if (btn) btn.textContent = original;
        }, 2000);
      }
    } catch {
      alert('Could not copy. Code: ' + code);
    }
  }
}

/**
 * Auto-apply the generated discount code to the customer's Shopify cart.
 * Returns true on success, false if Shopify rejected the code or the
 * request failed. On false the modal falls back to "copy this code".
 *
 * Uses POST /cart/update.js with {discount: code} (Storefront Ajax). Works
 * with both populated and empty carts — Shopify holds the code until the
 * first line item is added if needed.
 */
async function applyDiscountToCart(code) {
  if (!code) return false;
  try {
    const r = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }),
    });
    if (!r.ok) return false;
    const json = await r.json().catch(() => null);
    // Shopify returns the cart object on success. We could verify the code
    // landed in cart.discount_codes / cart.cart_level_discount_applications,
    // but a 200 OK is enough — Shopify only returns 200 if the discount was
    // accepted (invalid/expired codes return 422).
    return !!json;
  } catch {
    return false;
  }
}

if (!customElements.get('loyalty-portal')) {
  customElements.define('loyalty-portal', LoyaltyPortal);
}
